import { BREADCRUMBS_ALIGNMENT_OPTIONS, BREADCRUMBS_DENSITY_OPTIONS } from '@/common/constants';
import { BreadcrumbsAlignment, BreadcrumbsDensity, BreadcrumbsPosition } from '@/common/types/ui';
import { ArrowChevronRightIcon } from '@eventbrite/marmalade/icons';
import classNames from 'classnames';
import React from 'react';
import styles from './Breadcrumbs.module.scss';

export interface BreadcrumbItem {
  label: string;
  link?: string;
  isCurrentPage?: boolean;
  dataHeapId?: string;
}

export interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
  density?: BreadcrumbsDensity;
  alignment?: BreadcrumbsAlignment;
  showTrailingDivider?: boolean;
  showLeadingDivider?: boolean;
  position?: BreadcrumbsPosition;
  showInverse?: boolean;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  className,
  density = BREADCRUMBS_DENSITY_OPTIONS.DEFAULT,
  showTrailingDivider = false,
  showLeadingDivider = false,
  alignment = BREADCRUMBS_ALIGNMENT_OPTIONS.LEFT,
  showInverse = false,
}) => {
  if (!items) return null;
  const containerClassName = classNames(
    styles.breadcrumbs,
    styles[`breadcrumbs_density-${density}`],
    styles[`breadcrumbs_alignment-${alignment}`],
    showLeadingDivider && styles.breadcrumbs_leadingDivider,
    showTrailingDivider && styles.breadcrumbs_trailingDivider,
    showInverse && styles.breadcrumbs_inverse,
    className,
  );

  return (
    <nav className={containerClassName} aria-label="Breadcrumb navigation">
      <ol className={styles.breadcrumbsList}>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isCurrentPage = item.isCurrentPage || isLast;

          return (
            <li key={index} className={styles.breadcrumbItem}>
              {item.link && !isCurrentPage ? (
                <>
                  <a
                    href={item.link}
                    className={styles.breadcrumbLink}
                    data-heap-id={item.dataHeapId}
                  >
                    {item.label}
                  </a>
                  <ArrowChevronRightIcon
                    size="XS"
                    color={
                      showInverse
                        ? 'var(--marm-color-inverse-fg-base)'
                        : 'var(--marm-color-action-fg-base)'
                    }
                    className={styles.breadcrumbSeparator}
                  />
                </>
              ) : (
                <a
                  href={item.link}
                  className={classNames(isCurrentPage && styles.breadcrumbCurrent)}
                  data-heap-id={item.dataHeapId}
                >
                  {item.label}
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
